import { BASE_URL } from "../../global";
import { toast } from "react-toastify";
import { makeChunks } from "../helper/helpers";
import unitConvertor, {
  getBaseUnit,
} from "../../utils/components/unitConversion/unitConvertor";
import axiosWithToken from "../../utils/components/axiosTokenConfig";

import {
  formatNumberToSpecialValues,
  formatSpecialValuesToNumber,
} from "../../utils/utils";
import {
  containsCustomUnitPair,
  customUnitConversion,
} from "../../utils/components/unitConversion/customUnitConvertor";

const tablesData = {};

const TABLES_MAP = {
  datasheet: "datasheets",
  instrument: "instruments",
  datasheeetStaticTable: "datasheetStaticTables",
  datasheetStaticReading: "datasheetStaticReadings",
  standard: "standards",
  standardRange: "standardRanges",
  supportiveInstrumentRange: "instruments",
  srfInstrument: "srfInstruments",
  settings: "settings",
};

function findTokens(str) {
  const regex = /\$[\w.:' ]+/g;
  let matches = str?.match(regex);

  if (matches) {
    return matches;
  } else {
    return [];
  }
}

const fetchTables = async (queries,sqlQueries) => {
  // let chunks = makeChunks(Object.entries(queries));
  let SQLchunks = makeChunks(Object.entries(sqlQueries));

  for (let chunk of SQLchunks) {
    await Promise.all(
      chunk.map((e) =>
        axiosWithToken
          .post("dynamic",{"query":e[1]}, {headers:{requestType: 'fetchData'}})
          .then((res) => ({ name: e[0], value: res.data[0] }))
      )
    )
      .then((res) => {
        res.forEach((e) => (tablesData[e.name] = e.value));
      })
      .catch((err) => {
        console.error("err : ", err);
      });
  }
};

const parseTokens = (tokens, attributeTables, tables, idMaps) => {
  let tp = [];

  tokens = tokens
    .map((token) => {
      // trim unwanted spaces
      token = token.trim();

      // remove additional attributes from token '.unit', '.all', '.higher'
      if (token.includes(".unit")) {
        token = token.replaceAll(".unit", "");
      }
      if (token.includes(".all")) {
        token = token.replaceAll(".all", "");
      }
      if (token.includes(".higher")) {
        token = token.replaceAll(".higher", "");
      }

      tp = token.substring(1).split(".");
      tp[2] = tp[2]?.split(":");
      return tp;
    })
    .map((e, i) => {
      if (!TABLES_MAP[e[0]]) {
        toast.warning("found wrong table in equation : " + tokens[i]);
        return null;
      }
      if (e[2]) {
        attributeTables[
          `${BASE_URL}${TABLES_MAP[e[0]]}?_where=(${
            e[2][0]
          },eq,${e[2][1]?.replaceAll("'", "")})&_fields=${e[1]}`
        ] = [e[1]];
        return null;
      } else if (idMaps[e[0]]) {
        let id = idMaps[e[0]];
        let tId = `${BASE_URL}${TABLES_MAP[e[0]]}_${id}`;
        if (tables[tId]) tables[tId][0][e[1]] = true;
        else tables[tId] = [{ [e[1]]: true }, `${e[0]}_${id}`, id];
        return null;
      }
      return e;
    })
    .filter((e) => e);
  return tokens;
};

export const generateTableData = async (
  unceratintyFactors,
  readings,
  tableId,
  datasheetId,
  instrumentId,
  standardToUncertaintyFactorMap
) => {
  let conditions = unceratintyFactors.map((e) => [
    e.id,
    JSON.parse(e.showcondition),
  ]);
  let configs = unceratintyFactors.map((e) => [
    e.id,
    JSON.parse(e.sourceconfig),
  ]);

  let sensitiveCoefficientFormulas = unceratintyFactors.map((e) => [
    e.id,
    JSON.parse(e.sensitives),
  ]);

  let valueAdjustmentFormulas = unceratintyFactors.map((e) => [
    e.id,
    JSON.parse(e.valueAdjustment),
  ]);

  let standardRanges = readings.map((reading) => [
    ...reading.standardRanges,
    reading.supportiveRanges,
  ]);

  let readingIds = readings.map((reading) => reading.id);
  let tokensDict = {};
  let tables = {};
  let attributeTables = {};
  let tablesIdMap = {
    settings: null,
    datasheet: datasheetId,
    srfInstrument: datasheetId,
    instrument: instrumentId,
    datasheeetStaticTable: tableId,
    readings: readingIds.map((readingId, i) => ({
      datasheetStaticReading: readingId,
      masters: [
        ...standardRanges[i].map((std) => ({
          standard: std[0],
          standardRange: std[1],
        })),
      ],
    })),
  };

  let tokens = [];

  conditions.forEach((condition, i) => {
    tokens = [
      ...findTokens(Object.entries(condition[1]).flat().join(",")),
      ...findTokens(Object.entries(configs[i][1]).flat().join(",")),
      ...findTokens(
        Object.entries(sensitiveCoefficientFormulas[i][1]).flat().join(",")
      ),
      ...findTokens(
        Object.entries(valueAdjustmentFormulas[i][1] || {})
          .flat()
          .join(",")
      ),
    ];
    tokens = parseTokens(tokens, attributeTables, tables, tablesIdMap);

    if (tokens?.length > 0) tokensDict[condition[0]] = tokens;
  });

  let additionalTokens = [];
  unceratintyFactors.forEach((uncertaintyFactor) => {
    if (uncertaintyFactor.additionalReferences) {
      additionalTokens = [
        ...additionalTokens,
        ...uncertaintyFactor.additionalReferences,
      ];
    }
    tokens = parseTokens(
      additionalTokens,
      attributeTables,
      tables,
      tablesIdMap
    );

    tokensDict[0] = [...(tokensDict[0] || []), ...tokens];
  });

  //  query generator
  let query, id;
  Object.keys(tokensDict).forEach((ufId) => {
    tokensDict[ufId].forEach((token) => {
      readingIds.forEach((_, i) => {
        query = null;
        id = null;
        if (token[0]?.includes("standard")) {
          // process tokens associated with standards or standard ranges
          for (let j = 0; j < standardRanges[i].length; j++) {
            let stdRange = standardRanges[i][j];
            if (
              standardToUncertaintyFactorMap[stdRange[0]]?.includes(ufId) ||
              ufId == 0
            ) {
              id = tablesIdMap.readings[i].masters[j][token[0]];
              if (id) {
                query = `${BASE_URL}${TABLES_MAP[token[0]]}_${id}`;
                if (tables[query]) {
                  tables[query][0][token[1]] = true;
                } else {
                  tables[query] = [
                    { [token[1]]: true },
                    `${token[0]}_${id}`,
                    id,
                  ];
                }
                if (token[0] == "standardRange") {
                  if (!tables[query][0]["rangeName"]) {
                    tables[query][0]["rangeName"] = true;
                  }
                    if (!tables[query][0]["reactionTime"]) {
                    tables[query][0]["reactionTime"] = true;
                  }
                }
                if (token[0] == "standard") {
                  tables[query][0]["type"] = true;
                }
              }
            }
          }
        } else {
          // process tokens assocated with datasheet reading
          id = tablesIdMap.readings[i][token[0]];
          if (id) {
            query = `${BASE_URL}${TABLES_MAP[token[0]]}_${id}`;
            if (tables[query]) {
              tables[query][0][token[1]] = true;
            } else {
              tables[query] = [{ [token[1]]: true }, `${token[0]}_${id}`, id];
            }
          }
        }
      });
    });
  });

  let queries = {};
  Object.entries(attributeTables).forEach((e) => (queries[e[1]] = e[0]));
  Object.entries(tables).forEach((e) => {
    queries[e[1][1]] = `${e[0].split("_")[0]}?_fields=${Object.keys(
      e[1][0]
    ).join(",")}&_where=(id,eq,${e[1][2]})`;
  });

const sqlQueries = Object.fromEntries(
  Object.entries(queries).map(([key, url]) => {
    // Extract part after /api/ and before '?' for table name
    const tableMatch = url.match(/\/api\/([^?]+)/);
    const table = tableMatch ? tableMatch[1] : "";

    const fields = (url.match(/_fields=([^&]*)/) || [])[1] || "*";
    const whereParts = (url.match(/_where=\(([^)]*)\)/) || [])[1]?.split(",") || [];
    const [col, op, val] = whereParts;
    const sqlOp = op === "eq" ? "=" : op;
    const where = whereParts.length ? ` where ${col} ${sqlOp} ${val}` : "";
    return [key, `select ${fields} from ${table}${where}`];
  })
);

  await fetchTables(queries,sqlQueries);
};

export const convertToStandardUnit = (val) => {
  let originalValueType = "number";
  let currentValueType = "number";
  [val, originalValueType, currentValueType] = formatSpecialValuesToNumber(val);

  return val;
};

export const convertUnit = (val, from, to, baseValue = null) => {
  let res = val;
  val = val || 0;

  // convert base value to standard value
  baseValue = convertToStandardUnit(baseValue);

  if (from && to) {
    if (from.trim() === "ppm") {
      res = (val / 1000000) * (baseValue || 1);
    } else if (to.trim() === "ppm") {
      res = val * 1000000 * (baseValue || 1);
    } else if (from.trim() === "%" && baseValue != null) {
      res = (Math.abs(Number(val)) * Math.abs(Number(baseValue))) / 100;
    } else if (to.trim() === "%" && baseValue != null) {
      res = (Math.abs(Number(val)) * 100) / Math.abs(Number(baseValue));
    } else {
      res = unitConvertor(val, from, to);
    }
  }

  return res;
};

export const convertUnitWrap = (val, from, to) => {
  return Number(unitConvertor(val, from, to));
};

export const resolveToken = (token, selectors) => {
  let value = null;
  token = token.trim();
  let tp = token.substring(1).split(".");
  tp[2] = tp[2]?.split(":");
  if (tp[2]?.[0] == "unit") {
    token = `${tp[0]}_${selectors[tp[0]]}`;
    value = tablesData[token]?.[tp[1]]?.split("#")[1];
  } else if (tp[2]?.[0] == "all") {
    value = [];
    let tokens = Object.keys(tablesData).filter((e) => {
      if (e.startsWith(`${tp[0]}_`)) {
        return e;
      }
    });
    tokens.forEach((e) => {
      if (tablesData[e]?.[tp[1]]) {
        // TODO: added static logic to skip supportive instrument, plese write generic logic irrespective column or table
        if (tp[0] == "standard") {
          if (tablesData[e]?.type == 1) {
            value.push(tablesData[e]?.[tp[1]]);
          }
        } else {
          value.push(tablesData[e]?.[tp[1]]);
        }
      }
    });
    value = value.join(",");
  } else if (tp[2]?.[0] == "higher") {
    token = `${tp[0]}_${selectors[tp[0]]}`;
    value = tablesData[token]?.[tp[1]];
    value = value?.split("|");
    value = value.map((e) => {
      return e?.split("#")?.[0];
    });
    value = String(Math.max(...value));
  } else if (!tp[2]) {
    token = `${tp[0]}_${selectors[tp[0]]}`;
    value = tablesData[token]?.[tp[1]];
  }
  return value;
};

export const resolveCondition = (conditions, selectors) => {
  let trueCondition = "default";
  for (const element of conditions) {
    let condition = element;
    if (condition == "default") continue;
    let updatedCondition = condition;
    let tokens = findTokens(condition).filter((token) => token != "default");
    for (const element of tokens) {
      let token = element;
      let tokenResult = resolveToken(token, selectors);
      if (/\D/.test(tokenResult) || tokenResult == "") {
        tokenResult = String(tokenResult).replaceAll("'", "sqt"); // sqt = single quote
        tokenResult = `'${tokenResult}'`;
      }
      updatedCondition = updatedCondition.replace(token, tokenResult);
    }

    // eslint-disable-next-line no-new-func
    let res = Function("return " + updatedCondition)();
    if (res === true) {
      trueCondition = condition;
      break;
    }
  }
  return trueCondition;
};

export const resolveFormula = (
  sourceFormula,
  updatedFormula,
  fixedUnit,
  selectors,
  referenceData,
  fallbackUnit,
  rangeCol
) => {
  let resolvedFormula = updatedFormula; // init
  if (updatedFormula !== "") {
    let tokens = findTokens(sourceFormula);

    // 1. iterate tokens and resolve values, units
    let TokenValues = {};
    for (let i = 0; i < tokens?.length; i++) {
      let rawValue = resolveToken(tokens[i], selectors);
      rawValue = rawValue == null || rawValue == "" ? "NA" : rawValue;
      if (rawValue.includes("|")) {
        // process for multiple values
        let values = rawValue?.split("|");
        let unit = values[0]?.split("#")[1] || "";
        let value = values.map((e) => e?.split("#")[0]).join(",");
        TokenValues[tokens[i]] = {
          value: value,
          unit: unit,
        };
      } else {
        // process for single value
        let value = rawValue?.split("#")[0] || 0;
        let unit = rawValue?.split("#")[1] || "";
        TokenValues[tokens[i]] = {
          value: value,
          unit: unit,
        };
      }
    }

    // 2. find desired unit for final uncertainty factor value
    // process: if tokenValues containes different unit types then consider fallbackUnit as desired unit, else consider unit from tokenValues
    let desiredUnit = fixedUnit;
    if (!desiredUnit) {
      let units = Object.values(TokenValues).map((e) => e.unit);
      units = units.filter((e) => e !== "");
      units = [...new Set(units)];
      desiredUnit = units?.length > 1 ? fallbackUnit : units[0] || null;
    }

    // 3. replace token with respective values
    let baseUnit = null;

    for (let i = 0; i < tokens?.length; i++) {
      let value = TokenValues[tokens[i]]?.value || 0;
      let unit = TokenValues[tokens[i]]?.unit || "";
      value = unitConvertor(value, unit);
      if (unit && fallbackUnit && baseUnit !== desiredUnit) {
        // preapre base value: 1st column of reading table (reading range)
        let baseValue = referenceData?.readingRow?.[rangeCol]?.[0];

       if (unit?.toLowerCase().includes("fsd")) {
              let rangeValue;
              let rangeUnit;

              // 1️⃣ First Priority: reactionTime
              let reactionValue = resolveToken("$standardRange.reactionTime", selectors);
              let reactionPart = reactionValue?.split("|")?.[1] || reactionValue;

              rangeValue = reactionPart?.split("#")?.[0];
              rangeUnit = reactionPart?.split("#")?.[1];

              // 2️⃣ Second Priority: masterrange
              if (!rangeValue || isNaN(Number(rangeValue))) {
                let masterRange = resolveToken("$standard.masterrange", selectors);

                if (masterRange?.includes("|")) {
                  const part1 = masterRange.split("|")[0];
                  const part2 = masterRange.split("|")[1];

                  const value1 = part1?.split("#")?.[0];
                  const unit1 = part1?.split("#")?.[1];

                  const value2 = part2?.split("#")?.[0];
                  const unit2 = part2?.split("#")?.[1];

                  if (unit1 === unit2 && !isNaN(value1) && !isNaN(value2)) {
                    rangeValue = Number(value1) + Number(value2);
                    rangeUnit = unit1;
                  } else {
                    rangeValue = value2 || value1;
                    rangeUnit = unit2 || unit1;
                  }
                } else {
                  rangeValue = masterRange?.split("#")?.[0];
                  rangeUnit = masterRange?.split("#")?.[1];
                }
              }

              // 3️⃣ Third Priority: rangeName
              if (!rangeValue || isNaN(Number(rangeValue))) {
                let rangeName = resolveToken("$standardRange.rangeName", selectors);
                let rangePart = rangeName?.split("|")?.[1] || rangeName;

                rangeValue = rangePart?.split("#")?.[0];
                rangeUnit = rangePart?.split("#")?.[1];
              }

              if(rangeUnit!=desiredUnit){
              rangeValue = unitConvertor(Number(rangeValue), rangeUnit, desiredUnit);
              }

              baseValue = Number(rangeValue);

            if(unit?.toLowerCase().includes("ppm")){
              unit = "ppm";
            }else{
            unit = "%";
            }

            }

            value = unitConvertor(value, unit, desiredUnit, baseValue);
                
      }
      resolvedFormula = String(updatedFormula)?.replace(tokens[i], value);
      updatedFormula = String(updatedFormula)?.replace(tokens[i], value);
    }

    // 4. evaluate formula
    // eslint-disable-next-line no-new-func
    let resultValue = "";
    try {
      updatedFormula = updatedFormula?.replaceAll("$", "");
      resolvedFormula = resolvedFormula?.replaceAll("$", "");
      // eslint-disable-next-line no-new-func
      resultValue = Function("return " + updatedFormula)();
    } catch (e) {
      console.error("Error in resolving formula: ", e);
      console.error("Formula: ", updatedFormula);
      resultValue = "[wrong formula] " + updatedFormula;
    }

    resultValue = String(resultValue) + "#" + (desiredUnit || "");

    return [resultValue, resolvedFormula];
  } else return ["0", ""];
};

export const resolveUncertaintyValue = (
  unceratintyFactor,
  selectors,
  referenceData,
  fallbackUnit,
  rangeCol
) => {
  let dataSourceDetails = {};

  // 1. resolve condition and process source config
  // find values from table references
  let config = JSON.parse(unceratintyFactor.sourceconfig);
  let sourceValueUnit = unceratintyFactor.sourceValueUnit;
  let selectedCondition = resolveCondition(Object.keys(config), selectors);
  let sourceFormula = config[selectedCondition];
  let updatedFormula = sourceFormula;
  let res = resolveFormula(
    sourceFormula,
    updatedFormula,
    sourceValueUnit,
    selectors,
    referenceData,
    fallbackUnit,
    rangeCol
  );
  let sourceConfigRes = res[0];
  let resolvedFormula = res[1];

  dataSourceDetails = {
    ...dataSourceDetails,
    updatedFormula: updatedFormula,
    resolvedFormula: resolvedFormula,
  };

  // 2. resolve and process sensitive coefficientk
  // find values from table references
  let sensitiveCoefficient = JSON.parse(unceratintyFactor.sensitives);
  let sensitiveCoefficientUnit = unceratintyFactor.sensitiveCoefficientUnit;
  selectedCondition = resolveCondition(
    Object.keys(sensitiveCoefficient),
    selectors
  );
  sourceFormula = sensitiveCoefficient[selectedCondition];
  res = resolveFormula(
    sourceFormula,
    sourceFormula,
    sensitiveCoefficientUnit,
    selectors,
    fallbackUnit
  );
  let sensitiveCoefficientRes = res[0];

  dataSourceDetails = {
    ...dataSourceDetails,
    sensitiveCoefficientFormula: sourceFormula,
  };

  // 2. resolve and process valueAdjustment
  // find values from table references
  let valueAdjustmentRes = "0";
  let valueAdjustment = JSON.parse(unceratintyFactor.valueAdjustment);
  if (valueAdjustment) {
    let valueAdjustmentUnit = unceratintyFactor.valueAdjustmentUnit;
    selectedCondition = resolveCondition(
      Object.keys(valueAdjustment),
      selectors
    );

    valueAdjustmentRes = valueAdjustment[selectedCondition];
    resolvedFormula = valueAdjustment[selectedCondition];
  }

  dataSourceDetails = {
    ...dataSourceDetails,
    valueAdjustmentFormula: sourceFormula,
  };

  return [
    sourceConfigRes,
    sensitiveCoefficientRes || 1,
    valueAdjustmentRes || 0,
    dataSourceDetails,
  ];
};

// TODO: simplify this function
const evaluate = (uncertaintyContribution, operator, valueAdjustmentValue) => {
  valueAdjustmentValue = Number(valueAdjustmentValue) || 0;
  if (operator === "-")
    uncertaintyContribution = uncertaintyContribution - valueAdjustmentValue;
  if (operator === "+")
    uncertaintyContribution = uncertaintyContribution + valueAdjustmentValue;
  if (operator === "*")
    uncertaintyContribution = uncertaintyContribution * valueAdjustmentValue;
  if (operator === "/")
    uncertaintyContribution = uncertaintyContribution / valueAdjustmentValue;
  if (operator === "%")
    uncertaintyContribution = uncertaintyContribution % valueAdjustmentValue;

  return uncertaintyContribution;
};

export const calculateUncertaintyContribution = async (
  ufLimitXi,
  probabiltyDistribution,
  sensitiveCoefficient,
  valueAdjustment,
  desiredUnit,
  baseValues,
  rangeColUnit
) => {
  // 1.0 prepare values
  let [ufLimitXiValue, ufLimitXiUnit] = ufLimitXi?.split("#") || [0, ""];
  // 1.1 if value in NaN then return as it is

  let [sensitiveCoefficientValue, sensitiveCoefficientUnit] = String(
    sensitiveCoefficient
  )?.split("#") || [0, ""];

  let operator = valueAdjustment[0];
  let valueAdjustmentValue = valueAdjustment.slice(1);

  // 2. calculate uncertaintyContribution
  let uncertaintyContribution = se(
    se(ufLimitXiValue, "/", probabiltyDistribution, {
      unit: {
        lhs: ufLimitXiUnit,
        rhs: null,
      },
    }),
    "*",
    sensitiveCoefficientValue,
    {
      unit: {
        lhs: ufLimitXiUnit,
        rhs: null,
      },
    }
  );

  // 2.1 add value adjustment
  uncertaintyContribution = evaluate(
    uncertaintyContribution,
    operator,
    valueAdjustmentValue
  );

  // 3. convert value to desired unit
  let fromUnit =
    sensitiveCoefficientUnit && sensitiveCoefficientUnit !== ""
      ? sensitiveCoefficientUnit
      : ufLimitXiUnit;
  let baseValue = baseValues["percentBaseValue"];

  // 3.1 handle custom unit conversion cases
  if (fromUnit.toLowerCase() === "%fsd" || fromUnit.toLowerCase() === "%fs") {
    baseValue = baseValues["factorFSDBaseValue"];
    fromUnit = "%";
  }

  
  // this is only for ActionInstrument Branch ******
  // 3.2 explicit special-case unit conversions (checked before the generic
  // custom/standard conversion paths so they always take priority when the
  // from/to units match one of these pairs).
  const normalizedFromUnit = String(fromUnit || "").trim().toLowerCase();
  const normalizedDesiredUnit = String(desiredUnit || "").trim().toLowerCase();

  // if (normalizedFromUnit === "mg" && normalizedDesiredUnit === "ml") {
  //   // mg -> ml : value / 1000
  //   uncertaintyContribution = Number(uncertaintyContribution) / 1000;
  // } else if (normalizedFromUnit === "g" && normalizedDesiredUnit === "µl") {
  //   // g -> µL : value * 1000
  //   uncertaintyContribution = Number(uncertaintyContribution) * 1000;
  // } else 
    
    if (containsCustomUnitPair(fromUnit, desiredUnit)) {
    uncertaintyContribution = await customUnitConversion(
      Number(uncertaintyContribution),
      fromUnit,
      desiredUnit,
      null, //current row
      null // base value
    );
  } else {

    const isPercent = fromUnit?.trim() === "%";
    const needsRangeConversion = isPercent && desiredUnit !== rangeColUnit;

  if (needsRangeConversion) {
    uncertaintyContribution = unitConvertor(
      uncertaintyContribution,
      fromUnit,
      desiredUnit,
      baseValue
    );

  }

  const sourceUnit = needsRangeConversion ? rangeColUnit : fromUnit;

uncertaintyContribution = unitConvertor(
  uncertaintyContribution,
  sourceUnit,
  desiredUnit,
  baseValue
);

  }

  return String(uncertaintyContribution + `#${desiredUnit}`);
};

export const se = (o1val, op, o2val, constraints = {}) => {
  let baseUnit = getBaseUnit(constraints?.unit?.lhs);

  if (["s", "°"].includes(baseUnit)) {
    if (constraints?.unit && baseUnit) {
      if (constraints?.unit?.lhs) {
        o1val = unitConvertor(o1val, constraints?.unit?.lhs, baseUnit);
      }
      if (constraints?.unit?.rhs) {
        o2val = unitConvertor(o2val, constraints?.unit?.rhs, baseUnit);
      }
    }
  }
  o1val = Number(o1val);
  o2val = Number(o2val);

  let res = 0;
  if (op === "+") {
    res = o1val + o2val;
  } else if (op === "-") {
    res = o1val - o2val;
  } else if (op === "*") {
    res = o1val * o2val;
  } else if (op === "/") {
    res = o1val / o2val;
  } else if (op === "%") {
    res = o1val % o2val;
  } else if (op == "sqrt") {
    res = Math.sqrt(o1val);
  } else if (op == "pow") {
    res = Math.pow(o1val, o2val);
  }

  if (constraints?.isAbs && !isNaN(res)) {
    res = Math.abs(res);
  }

  if (["s", "°"].includes(baseUnit)) {
    if (constraints?.unit?.lhs && baseUnit) {
      res = unitConvertor(res, baseUnit, constraints?.unit?.lhs);
    }
  }

  return res;
};
